import React, { useEffect, useState, useRef } from 'react';
/**
 * MultiplicationGridRenderer — Fill hidden cells in a multiplication table.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 */
import { SparklesIcon } from 'lucide-react';
import { QuestionRendererProps } from './types';
export function MultiplicationGridRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const mode = config.mode || 'single_table';
  const prevValidateRef = useRef(false);
  const [filledCells, setFilledCells] = useState<Record<string, string>>({});
  useEffect(() => {
    setFilledCells({});
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  const totalHiddenCells = config.hiddenCells?.length || 0;
  useEffect(() => {
    const filledCount = Object.values(filledCells).filter(v => v.trim() !== '').length;
    onReadyChange?.(filledCount === totalHiddenCells && totalHiddenCells > 0);
  }, [filledCells, totalHiddenCells, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onAnswer(JSON.stringify(filledCells));
    }
  }, [shouldValidate, filledCells, onAnswer]);
  const handleInputChange = (key: string, value: string) => {
    setFilledCells(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const filledCount = Object.values(filledCells).filter(v => v.trim() !== '').length;
  const cellInputClass = (val: string) => `w-full h-full text-center font-bold rounded-md outline-none transition-all duration-200 ${val ? 'bg-indigo-50 border-2 border-indigo-300 text-indigo-800' : 'bg-amber-50 border-2 border-amber-300 text-gray-800'} focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:bg-white shadow-inner`;
  const headerCellClass = 'w-10 h-10 sm:w-12 sm:h-12 bg-indigo-600 text-white font-bold rounded-md text-center align-middle shadow-sm';
  const dataCellClass = 'w-10 h-10 sm:w-12 sm:h-12 bg-white border border-gray-200 text-gray-800 font-medium rounded-md text-center align-middle shadow-sm';
  const renderSingleTable = () => {
    const baseNumber = config.baseNumber || 1;
    const range: number[] = config.range || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    const hiddenCells: number[] = config.hiddenCells || [];
    return <div className="overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-7259">
        <table className="border-separate border-spacing-1 w-max mx-auto" data-id="element-7260">
          <tbody data-id="element-7261">
            <tr data-id="element-7262">
              <td className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-800 text-white font-bold rounded-md text-center align-middle shadow-sm" data-id="element-7263">
                ×
              </td>
              {range.map((m, i) => <td key={`h-${i}`} className={headerCellClass} data-id="element-7264">
                  {m}
                </td>)}
            </tr>
            <tr data-id="element-7265">
              <td className={headerCellClass} data-id="element-7266">{baseNumber}</td>
              {range.map((multiplier, idx) => {
              if (hiddenCells.includes(idx)) {
                const key = String(idx);
                return <td key={`c-${idx}`} className="w-10 h-10 sm:w-12 sm:h-12" data-id="element-7267">
                      <input type="text" inputMode="numeric" pattern="[0-9]*" value={filledCells[key] || ''} onChange={e => handleInputChange(key, e.target.value)} className={cellInputClass(filledCells[key] || '')} data-id="element-7268" />
                    </td>;
              }
              return <td key={`c-${idx}`} className={dataCellClass} data-id="element-7269">
                    {baseNumber * multiplier}
                  </td>;
            })}
            </tr>
          </tbody>
        </table>
      </div>;
  };
  const renderCrossTable = () => {
    const rows: number[] = config.rows || [];
    const cols: number[] = config.cols || [];
    const hiddenCells: Array<{
      row: number;
      col: number;
    }> = config.hiddenCells || [];
    const isHidden = (r: number, c: number) => hiddenCells.some(cell => cell.row === r && cell.col === c);
    return <div className="overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-7270">
        <table className="border-separate border-spacing-1 w-max mx-auto" data-id="element-7271">
          <tbody data-id="element-7272">
            <tr data-id="element-7273">
              <td className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-800 text-white font-bold rounded-md text-center align-middle shadow-sm" data-id="element-7274">
                ×
              </td>
              {cols.map((c, i) => <td key={`hc-${i}`} className={headerCellClass} data-id="element-7275">
                  {c}
                </td>)}
            </tr>
            {rows.map((rowVal, rIdx) => <tr key={`r-${rIdx}`} data-id="element-7276">
                <td className={headerCellClass} data-id="element-7277">{rowVal}</td>
                {cols.map((colVal, cIdx) => {
              if (isHidden(rIdx, cIdx)) {
                const key = `${rIdx}-${cIdx}`;
                return <td key={`cc-${rIdx}-${cIdx}`} className="w-10 h-10 sm:w-12 sm:h-12" data-id="element-7278">
                        <input type="text" inputMode="numeric" pattern="[0-9]*" value={filledCells[key] || ''} onChange={e => handleInputChange(key, e.target.value)} className={cellInputClass(filledCells[key] || '')} data-id="element-7279" />
                      </td>;
              }
              return <td key={`cc-${rIdx}-${cIdx}`} className={dataCellClass} data-id="element-7280">
                      {rowVal * colVal}
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>;
  };
  const progressPct = totalHiddenCells > 0 ? filledCount / totalHiddenCells * 100 : 0;
  return <div className="w-full flex flex-col space-y-6" data-id="element-7281">
      <div className="w-full bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-100 shadow-inner" data-id="element-7282">
        {mode === 'single_table' ? renderSingleTable() : renderCrossTable()}
      </div>
      <div className="flex flex-col items-center justify-center space-y-2" data-id="element-7283">
        <div className="flex items-center space-x-2 text-sm font-medium text-gray-600" data-id="element-7284">
          <SparklesIcon className={`w-4 h-4 ${filledCount === totalHiddenCells ? 'text-amber-500' : 'text-gray-400'}`} data-id="element-7285" />
          <span data-id="element-7286">
            {filledCount} van {totalHiddenCells} ingevuld
          </span>
        </div>
        <div className="w-48 h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-7287">
          <div className="h-full bg-indigo-500 transition-all duration-300 ease-out" style={{
          width: `${progressPct}%`
        }} data-id="element-7288" />
        </div>
      </div>
    </div>;
}